import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookingStepper } from '../components/quote-v2';
import {
  ConfirmationHero,
  BookingDetails,
  ReturnTransferCard,
  EmailConfirmation,
  TrustStrip,
  MobileConfirmationBar,
} from '../components/confirmation';
import { 
  WHATSAPP_CONFIG, 
  DataLayerEvents,
} from '../config/confirmationConfig';
import { AnalyticsEvents } from '../utils/analytics';
import useClarityPageReady from '../hooks/useClarityPageReady';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

const ConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [isLoadingBooking, setIsLoadingBooking] = useState(true);

  // Clarity page ready signal
  useClarityPageReady('confirmation');

  // Get booking data from URL params
  const sessionId = searchParams.get('session_id');
  const bookingRef = searchParams.get('ref') || searchParams.get('bookingRef') || searchParams.get('booking_ref');
  
  // Fetch booking details from backend
  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!bookingRef) {
        setIsLoadingBooking(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_BASE}/api/booking/ref/${bookingRef}`);
        const data = await response.json();
        
        if (data.success && data.booking) {
          const booking = data.booking;
          // Get pricing from pricing_snapshot if available
          const pricingSnapshot = booking.pricing_snapshot || {};
          const discount = pricingSnapshot.discount || booking.discount || 0;
          const subtotal = pricingSnapshot.subtotal || booking.subtotal || 0;
          // total is the final paid amount (after discount)
          const totalPaid = booking.total || pricingSnapshot.total || subtotal - discount || 0;
          
          setBookingData({
            bookingRef: booking.booking_ref,
            pickup: booking.pickup_location || 'N/A',
            dropoff: booking.dropoff_location || 'N/A',
            date: booking.trip_date || booking.pickup_date || '',
            time: booking.trip_time || booking.pickup_time || '',
            passengers: booking.passengers || 1,
            luggage: booking.luggage || 1,
            vehicleName: booking.vehicle_name || booking.vehicle_type || 'VIP Transfer',
            vehicleType: booking.vehicle_type || 'VAN',
            extras: booking.extras || [],
            totalPaid: parseFloat(totalPaid).toFixed(2),
            subtotal: parseFloat(subtotal).toFixed(2),
            paymentMethod: booking.payment_method || 'stripe',
            couponCode: pricingSnapshot.couponCode || booking.coupon_code || '',
            discount: parseFloat(discount).toFixed(2),
            email: booking.customer_email || '',
            status: booking.status,
            paymentStatus: booking.payment_status,
            currency: pricingSnapshot.currency || booking.currency || 'EUR',
            pickupType: booking.pickup_type || 'manual',
          });
        } else {
          // Fallback to URL params if no booking found
          setBookingData(getBookingFromUrlParams());
        }
      } catch (error) {
        console.error('Failed to fetch booking:', error);
        // Fallback to URL params
        setBookingData(getBookingFromUrlParams());
      } finally {
        setIsLoadingBooking(false);
      }
    };
    
    fetchBookingDetails();
  }, [bookingRef]);

  // Fallback function to get booking from URL params
  const getBookingFromUrlParams = () => {
    let passengerData = {};
    try {
      const passengerParam = searchParams.get('passenger');
      passengerData = passengerParam ? JSON.parse(passengerParam) : {};
    } catch (e) {
      console.error('Failed to parse passenger data');
    }

    let extras = [];
    try {
      const extrasParam = searchParams.get('extras');
      extras = extrasParam ? JSON.parse(extrasParam) : [];
    } catch (e) {
      console.error('Failed to parse extras');
    }

    return {
      bookingRef: bookingRef || `VGZ-${Date.now().toString().slice(-8)}`,
      pickup: searchParams.get('pickup') || 'Transfer Location',
      dropoff: searchParams.get('dropoff') || 'Destination',
      date: searchParams.get('date') || '',
      time: searchParams.get('time') || '',
      passengers: parseInt(searchParams.get('passengers')) || 1,
      luggage: parseInt(searchParams.get('luggage')) || 1,
      vehicleName: searchParams.get('vehicle') || 'VIP Transfer',
      vehicleType: searchParams.get('vehicleType') || 'VAN',
      extras,
      totalPaid: parseInt(searchParams.get('finalTotal')) || 0,
      paymentMethod: searchParams.get('paymentMethod') || 'stripe',
      couponCode: searchParams.get('couponCode') || '',
      discount: parseInt(searchParams.get('discount')) || 0,
      email: passengerData.email || '',
    };
  };
  
  // Poll Stripe payment status if we have a session_id
  useEffect(() => {
    const checkPaymentStatus = async () => {
      // Skip payment status check if we already have a booking ref
      // The webhook has already processed the payment
      if (!sessionId || bookingRef) {
        setPaymentStatus({ status: 'success', message: 'Payment received.' });
        return;
      }
      
      setIsCheckingPayment(true);
      let attempts = 0;
      const maxAttempts = 3; // Reduced from 5 - webhook should have processed already
      const pollInterval = 2000;
      
      const poll = async () => {
        if (attempts >= maxAttempts) {
          setPaymentStatus({ status: 'timeout', message: 'Payment verification timed out. Check your email for confirmation.' });
          setIsCheckingPayment(false);
          return;
        }
        
        try {
          const response = await fetch(`${API_BASE}/api/payment/checkout/status/${sessionId}`);
          
          // If server error, stop polling - the session might be invalid or already processed
          if (!response.ok) {
            console.log('Payment status check returned error, stopping poll');
            setPaymentStatus({ status: 'verified', message: 'Payment processed. Check your email for confirmation.' });
            setIsCheckingPayment(false);
            return;
          }
          
          const data = await response.json();
          
          if (data.payment_status === 'completed' || data.payment_status === 'paid') {
            setPaymentStatus({ status: 'success', message: 'Payment successful!', data });
            setIsCheckingPayment(false);
            toast.success('Payment confirmed! Thank you for your booking.');
            return;
          } else if (data.status === 'expired') {
            setPaymentStatus({ status: 'expired', message: 'Payment session expired.' });
            setIsCheckingPayment(false);
            return;
          }
          
          // Continue polling
          attempts++;
          setTimeout(poll, pollInterval);
        } catch (error) {
          console.error('Error checking payment status:', error);
          // Stop polling after error - assume payment was processed via webhook
          setPaymentStatus({ status: 'verified', message: 'Payment received. Check your email for confirmation.' });
          setIsCheckingPayment(false);
        }
      };
      
      poll();
    };
    
    checkPaymentStatus();
  }, [sessionId, bookingRef]);
  
  // Security check - verify order token exists
  useEffect(() => {
    // In production, verify order token with backend
    const hasValidSession = bookingRef || searchParams.get('session_id');
    
    if (!hasValidSession) {
      // Redirect to home if no valid session
      console.warn('No valid booking session found');
      // navigate('/');
    }
  }, [bookingRef, searchParams, navigate]);

  // Fire purchase event when booking data is loaded
  // IMPORTANT: This is the SINGLE source of truth for purchase event
  // GTM should listen for event: "purchase" to trigger Google Ads conversion
  useEffect(() => {
    if (!bookingData || !bookingData.bookingRef) return;
    
    // Only fire for confirmed/paid bookings
    const validStatuses = ['confirmed', 'paid', 'completed'];
    const bookingStatus = (bookingData.status || '').toLowerCase();
    const paymentStatus = (bookingData.paymentStatus || '').toLowerCase();
    const isValidBooking = validStatuses.includes(bookingStatus) || validStatuses.includes(paymentStatus);
    
    if (!isValidBooking) {
      console.log('[GTT Analytics] Purchase event SKIPPED - booking not confirmed:', {
        ref: bookingData.bookingRef,
        status: bookingData.status,
        paymentStatus: bookingData.paymentStatus,
      });
      return;
    }

    // Fire purchase event (async - waits for GTM readiness, handles dedupe internally)
    const firePurchase = async () => {
      const eventFired = await AnalyticsEvents.purchase({
        transactionId: bookingData.bookingRef,
        value: bookingData.totalPaid,
        currency: bookingData.currency || 'EUR',
        vehicleType: bookingData.vehicleType,
        vehicleName: bookingData.vehicleName,
        route: `${bookingData.pickup} -> ${bookingData.dropoff}`,
        pickup: bookingData.pickup,
        dropoff: bookingData.dropoff,
        passengers: bookingData.passengers,
        paymentMethod: bookingData.paymentMethod,
        couponCode: bookingData.couponCode,
      });

      // Microsoft Clarity - Track booking completion (secondary, non-blocking)
      if (eventFired && typeof window.clarity === 'function') {
        try {
          window.clarity('set', 'booking_ref', bookingData.bookingRef);
          window.clarity('set', 'booking_value', parseFloat(bookingData.totalPaid) || 0);
          const normalizedVehicle = (bookingData.vehicleType || 'unknown')
            .toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
          window.clarity('set', 'vehicle_type', normalizedVehicle);
          window.clarity('event', 'booking_complete');
        } catch (e) {
          console.warn('[Clarity] Failed to track booking:', e);
        }
      }
    };
    
    firePurchase();
  }, [bookingData]);

  // Handle copy booking reference
  const handleCopyRef = async () => {
    if (!bookingData) return;
    try {
      await navigator.clipboard.writeText(bookingData.bookingRef);
      setCopied(true);
      toast.success('Booking reference copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy');
    }
  };

  // Handle WhatsApp click
  const handleWhatsAppClick = () => {
    if (!bookingData) return;
    DataLayerEvents.pushWhatsAppClick(bookingData.bookingRef);
    
    const whatsappUrl = WHATSAPP_CONFIG.generateLink({
      bookingRef: bookingData.bookingRef,
      pickup: bookingData.pickup,
      date: bookingData.date,
      time: bookingData.time,
    });
    
    window.open(whatsappUrl, '_blank');
  };

  // Handle email/view details click
  const handleEmailClick = () => {
    // Scroll to booking details section
    document.getElementById('booking-details')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle return transfer booking
  const handleBookReturn = () => {
    if (!bookingData) return;
    DataLayerEvents.pushReturnTransferClick(bookingData.bookingRef);
    
    // Navigate to homepage with reversed route params
    // This will start a new booking flow
    const params = new URLSearchParams({
      pickup: bookingData.dropoff || '', // Reversed
      dropoff: bookingData.pickup || '', // Reversed
      passengers: (bookingData.passengers || 1).toString(),
      returnBooking: 'true',
    });
    
    // Navigate to homepage to start fresh booking
    navigate(`/?${params.toString()}`);
  };

  // Handle email resend
  const handleEmailResend = () => {
    if (!bookingData) return;
    DataLayerEvents.pushEmailResend(bookingData.bookingRef);
    toast.success('Confirmation email resent!');
  };

  // Show loading state
  if (isLoadingBooking) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <main className="pt-28 md:pt-32 pb-32 lg:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF8C00] mx-auto mb-4"></div>
              <p className="text-gray-400">Loading booking details...</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Show error if no booking data
  if (!bookingData) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <main className="pt-28 md:pt-32 pb-32 lg:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-4">Booking Not Found</h1>
              <p className="text-gray-400 mb-6">We couldn't find your booking details.</p>
              <button
                onClick={() => navigate('/')}
                className="bg-[#FF8C00] hover:bg-[#E67E00] text-white px-6 py-3 rounded-lg font-semibold"
              >
                Return to Homepage
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* noindex for booking flow pages */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      
      <Header />

      <main className="pt-28 md:pt-32 pb-32 lg:pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Booking Stepper - Step 5 Active (All Complete) */}
          <BookingStepper currentStep={5} />

          {/* Hero Confirmation Card */}
          <div className="mb-8">
            <ConfirmationHero
              bookingRef={bookingData.bookingRef}
              onCopyRef={handleCopyRef}
              copied={copied}
              onWhatsAppClick={handleWhatsAppClick}
              onEmailClick={handleEmailClick}
            />
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" id="booking-details">
            {/* Left: Booking Details */}
            <BookingDetails bookingData={bookingData} />

            {/* Right: Return Transfer */}
            <ReturnTransferCard
              onBookReturn={handleBookReturn}
              pickup={bookingData.pickup}
              dropoff={bookingData.dropoff}
            />
          </div>

          {/* Email Confirmation */}
          <div className="mb-6">
            <EmailConfirmation
              email={bookingData.email}
              bookingRef={bookingData.bookingRef}
              onResend={handleEmailResend}
            />
          </div>

          {/* Trust Strip */}
          <TrustStrip />

          {/* Policy Links */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-6 text-xs text-gray-400">
              <a href="/terms-of-service/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Terms & Conditions
              </a>
              <span>•</span>
              <a href="/privacy-policy/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Privacy Policy
              </a>
              <span>•</span>
              <a href="/cancellation-refund-policy/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Refund Policy
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile WhatsApp Bar */}
      <MobileConfirmationBar onWhatsAppClick={handleWhatsAppClick} />

      <Footer />
    </div>
  );
};

export default ConfirmationPage;
